/**
 * Module Life Performance — client API.
 *
 * Enveloppe les endpoints DRF montés sous `/api/performance/` (cf. backend
 * `Performance/urls.py`). Toutes les requêtes sont authentifiées (token).
 * Les listes DRF paginées sont déballées par `unwrap` (renvoie `results`).
 */
import {
  makeGetRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from '../../../utils/api'

const BASE = '/api/performance/'

const ok = (r) => r && r.status >= 200 && r.status < 300
const unwrap = (r) => {
  if (!ok(r)) return null
  const d = r.data
  if (d && typeof d === 'object' && Array.isArray(d.results)) return d.results
  return d
}

// CRUD générique sur une collection (`/api/performance/<resource>/`).
const collection = (resource) => ({
  list: async (params = '') =>
    unwrap(await makeGetRequest(`${BASE}${resource}/${params}`, {}, true)) || [],
  get: async (id) =>
    unwrap(await makeGetRequest(`${BASE}${resource}/${id}/`, {}, true)),
  create: async (body) =>
    unwrap(await makePostRequest(`${BASE}${resource}/`, body, {}, true)),
  update: async (id, body) =>
    unwrap(await makePatchRequest(`${BASE}${resource}/${id}/`, body, {}, true)),
  remove: async (id) =>
    ok(await makeDeleteRequest(`${BASE}${resource}/${id}/`, {}, true)),
})

export const performanceApi = {
  // --- Vue d'ensemble & dashboard (§D) ---
  getOverview: async () => unwrap(await makeGetRequest(`${BASE}overview/`, {}, true)),
  getDashboard: async () => unwrap(await makeGetRequest(`${BASE}dashboard/`, {}, true)),

  // --- §A Profil sportif ---
  sports: collection('sports'),
  preferences: collection('preferences'),

  // --- §B Objectifs ---
  goals: collection('goals'),
  addGoalMeasurement: async (goalId, body) =>
    unwrap(await makePostRequest(`${BASE}goals/${goalId}/add_measurement/`, body, {}, true)),

  // --- §C Plans & séances ---
  plans: collection('plans'),
  sessions: collection('sessions'),
  exercises: collection('exercises'),
  metrics: collection('metrics'),
  completeSession: async (id, body = {}) =>
    unwrap(await makePostRequest(`${BASE}sessions/${id}/complete/`, body, {}, true)),
  rescheduleSession: async (id, body) =>
    unwrap(await makePostRequest(`${BASE}sessions/${id}/reschedule/`, body, {}, true)),
  // Garnit une séance d'un bloc d'exercices spécialisé (muscu, golf, endurance…).
  applySessionTemplate: async (id, body = {}) =>
    unwrap(await makePostRequest(`${BASE}sessions/${id}/apply_template/`, body, {}, true)),
  // Palette d'exercices + bloc proposé pour un sport (ajout manuel).
  exerciseLibrary: async (sportId, sessionType = '') => {
    const q = `?sport=${sportId}${sessionType ? `&session_type=${sessionType}` : ''}`
    return unwrap(await makeGetRequest(`${BASE}exercise-library/${q}`, {}, true))
  },

  // --- §2 Intégrations & données réelles ---
  integrations: collection('integrations'),
  syncIntegration: async (id) =>
    unwrap(await makePostRequest(`${BASE}integrations/${id}/sync/`, {}, {}, true)),
  // Démarre l'OAuth d'un fournisseur web (Strava/Garmin) ; renvoie l'URL de
  // consentement, ou un statut « non configuré » si les clés serveur manquent.
  oauthStart: async (provider) =>
    makePostRequest(`${BASE}integrations/oauth/start/`, { provider }, {}, true),
  activities: collection('activities'),
  recovery: collection('recovery'),

  // --- §5 Planning ---
  calendar: collection('calendar'),
  generatePlan: async (body = {}) =>
    unwrap(await makePostRequest(`${BASE}planning/generate/`, body, {}, true)),

  // --- §6 Moteur & §7 Coach ---
  runEngine: async () =>
    unwrap(await makePostRequest(`${BASE}engine/run/`, {}, {}, true)),
  getNextSession: async () =>
    unwrap(await makeGetRequest(`${BASE}coach/next-session/`, {}, true)),
  generateWeeklyReview: async (body = {}) =>
    unwrap(await makePostRequest(`${BASE}coach/weekly-review/`, body, {}, true)),
  advices: collection('advices'),
  weeklyReviews: collection('weekly-reviews'),
}
